<template>
    <div
        v-if="text"
        ref="wrapperRef"
        class="tw:relative tw:inline-flex tw:items-center"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
    >
        <!-- DISPARADOR: Slot flexible con Fallback al botón original -->
        <slot
            name="trigger"
            :is-open="isOpen"
            :toggle="togglePin"
        >
            <button
                ref="triggerRef"
                type="button"
                data-test="admin-tooltip-trigger"
                :aria-label="resolvedAriaLabel"
                :aria-describedby="isOpen ? tooltipId : undefined"
                :class="[
                    'tw:inline-flex tw:items-center tw:justify-center tw:bg-transparent tw:border-0 tw:rounded-full tw:cursor-pointer tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-offset-1',
                    variantClasses[variant]?.trigger || variantClasses.primary.trigger
                ]"
                @click="togglePin"
            >
                <i :class="[icon, 'tw:text-base tw:leading-none']" aria-hidden="true"></i>
            </button>
        </slot>

        <!-- CONTENIDO DEL TOOLTIP. position: fixed con coordenadas en píxeles calculadas en
             JS para evitar scroll horizontal cuando el bubble queda cerca del borde. -->
        <div
            ref="bubbleRef"
            :id="tooltipId"
            role="tooltip"
            :aria-hidden="!isOpen"
            :style="bubbleStyle"
            class="tw:fixed tw:z-50 tw:w-max tw:max-w-[min(20rem,calc(100vw-1rem))] tw:md:max-w-[min(38rem,calc(100vw-1rem))] tw:px-2.5 tw:py-1.5 tw:text-xs tw:font-medium tw:rounded tw:pointer-events-none tw:break-words tw:text-pretty tw:transition-opacity tw:duration-300 tw:ease-out"
            :class="[
                variantClasses[variant]?.bubble || variantClasses.primary.bubble,
                isOpen ? 'tw:opacity-100' : 'tw:opacity-0'
            ]"
        >
            <slot>
                {{ text }}
            </slot>
        </div>
    </div>
</template>
<script setup>
    import { ref, computed, onMounted, onUnmounted } from 'vue'

    const props = defineProps({
        text: {
            type: String,
            default: ''
        },
        icon: {
            type: String,
            default: 'fas fa-info-circle'
        },
        variant: {
            type: String,
            default: 'primary',
            validator: v => ['danger', 'warning', 'success', 'primary', 'info'].includes(v)
        },
        delay: {
            type: Number,
            default: 200
        },
        ariaLabel: {
            type: String,
            default: ''
        }
    })

    // Colores por variante: cada una define el color del icono (500), del bg del bubble (50),
    // del texto del bubble (900) y del borde (200 — más oscuro que el bg).
    const variantClasses = {
        danger: {
            trigger: 'tw:text-red-500',
            bubble: 'tw:bg-red-50 tw:text-red-900 tw:border tw:border-red-200 tw:shadow-sm'
        },
        warning: {
            trigger: 'tw:text-amber-500',
            bubble: 'tw:bg-amber-50 tw:text-amber-900 tw:border tw:border-amber-200 tw:shadow-sm'
        },
        success: {
            trigger: 'tw:text-emerald-400',
            bubble: 'tw:bg-emerald-50 tw:text-emerald-900 tw:border tw:border-emerald-300'
        },
        primary: {
            trigger: 'tw:text-blue-500',
            bubble: 'tw:bg-blue-50 tw:text-blue-900 tw:border tw:border-blue-200 tw:shadow-sm'
        },
        info: {
            trigger: 'tw:text-cyan-500',
            bubble: 'tw:bg-cyan-50 tw:text-cyan-900 tw:border tw:border-cyan-200 tw:shadow-sm'
        }
    }

    // Generador de ID único compatible con cualquier versión de Vue
    const tooltipId = `tooltip-${Math.random().toString(36).substring(2, 9)}`
    const wrapperRef = ref(null)
    const triggerRef = ref(null)
    const bubbleRef = ref(null)

    const isPinned = ref(false)
    const isHoverVisible = ref(false)
    const isTouchDevice = ref(false)
    const effectivePlacement = ref('top-right')
    // Trigger reactivo: cambia en scroll/resize para que bubbleStyle se recalcule
    // (Vue no rastrea mutaciones del DOM, sólo refs).
    const scrollVersion = ref(0)

    const isOpen = computed(() => isPinned.value || isHoverVisible.value)
    const resolvedAriaLabel = computed(() => props.ariaLabel || props.text)

    let showTimeoutId = null

    // Orden de cascada: defecto top-right. Primero intenta mantener el vertical (top);
    // si no cabe horizontalmente, salta a top-left. Si tampoco cabe arriba, baja a bottom-right
    // y por último a bottom-left (totalmente opuesto).
    const POSITION_PRIORITY = ['top-right', 'top-left', 'bottom-right', 'bottom-left']
    // Fallback si el bubble aún no se ha medido (p. ej. en el primer show).
    // El cascade usa el tamaño real del bubble cuando está disponible.
    const FALLBACK_HEIGHT = 100
    const FALLBACK_WIDTH = 480
    const VIEWPORT_MARGIN = 8
    const TRIGGER_OFFSET = 4

    const clearShowTimeout = () => {
        if (showTimeoutId) {
            clearTimeout(showTimeoutId)
            showTimeoutId = null
        }
    }

    const closeAll = () => {
        clearShowTimeout()
        isPinned.value = false;
        isHoverVisible.value = false;
    }

    // Cascada de placement: defecto top-right, salta a posiciones opuestas según disponibilidad.
    // Usa el tamaño real del bubble cuando está disponible, con fallback prudente.
    // Añade un respiro de 32px al medir fit para que el bubble no quede pegado al borde.
    const decidePlacement = () => {
        if (!wrapperRef.value || typeof window === 'undefined') return

        const rect = wrapperRef.value.getBoundingClientRect()
        const bubbleRect = bubbleRef.value?.getBoundingClientRect()
        const bubbleWidth = (bubbleRect?.width || FALLBACK_WIDTH) + 32
        const bubbleHeight = (bubbleRect?.height || FALLBACK_HEIGHT) + 32

        const spaceAbove = rect.top
        const spaceBelow = window.innerHeight - rect.bottom
        const spaceLeft = rect.left
        const spaceRight = window.innerWidth - rect.right

        for (const position of POSITION_PRIORITY) {
            const [vertical, horizontal] = position.split('-')
            const verticalFits = vertical === 'top'
                ? spaceAbove >= bubbleHeight
                : spaceBelow >= bubbleHeight
            const horizontalFits = horizontal === 'right'
                ? spaceRight >= bubbleWidth
                : spaceLeft >= bubbleWidth

            if (verticalFits && horizontalFits) {
                effectivePlacement.value = position
                return
            }
        }

        effectivePlacement.value = 'top-right'
    }

    // Estilo inline del bubble: position: fixed con píxeles calculados desde la posición
    // del wrapper. Esto evita que el bubble cree scroll horizontal cuando se posiciona
    // cerca del borde (un position: absolute con bottom-full/left-full se salía del viewport).
    // bubbleStyle se recalcula también al hacer scroll, para que el bubble siga al trigger.
    const bubbleStyle = computed(() => {
        // Dependencia reactiva para forzar recálculo en scroll/resize.
        // eslint-disable-next-line no-unused-vars
        const _ = scrollVersion.value

        if (!wrapperRef.value || typeof window === 'undefined') return {}

        const rect = wrapperRef.value.getBoundingClientRect()
        const bubbleRect = bubbleRef.value?.getBoundingClientRect()
        const bubbleWidth = bubbleRect?.width || FALLBACK_WIDTH
        const bubbleHeight = bubbleRect?.height || FALLBACK_HEIGHT

        let top, left

        if (effectivePlacement.value.startsWith('top')) {
            top = rect.top - bubbleHeight - TRIGGER_OFFSET
        } else {
            top = rect.bottom + TRIGGER_OFFSET
        }

        if (effectivePlacement.value.endsWith('right')) {
            left = rect.right + TRIGGER_OFFSET
        } else {
            left = rect.left - bubbleWidth - TRIGGER_OFFSET
        }

        // Clamp al viewport
        if (left + bubbleWidth > window.innerWidth - VIEWPORT_MARGIN) {
            left = window.innerWidth - bubbleWidth - VIEWPORT_MARGIN
        }
        if (left < VIEWPORT_MARGIN) {
            left = VIEWPORT_MARGIN
        }
        if (top + bubbleHeight > window.innerHeight - VIEWPORT_MARGIN) {
            top = window.innerHeight - bubbleHeight - VIEWPORT_MARGIN
        }
        if (top < VIEWPORT_MARGIN) {
            top = VIEWPORT_MARGIN
        }

        return {
            top: `${top}px`,
            left: `${left}px`,
        }
    })

    const onScrollOrResize = () => {
        // Forzar recálculo del computed bubbleStyle (que depende de scrollVersion)
        // y re-evaluar la placement por si el wrapper se ha movido a otra zona.
        scrollVersion.value++
        decidePlacement()
    }

    const togglePin = () => {
        if (isPinned.value) {
            closeAll()
        } else {
            decidePlacement()
            isPinned.value = true
            clearShowTimeout()
            isHoverVisible.value = false
        }
    }

    const onMouseEnter = () => {
        if (isTouchDevice.value || isPinned.value) return
        clearShowTimeout()
        showTimeoutId = setTimeout(() => {
            decidePlacement()
            isHoverVisible.value = true
        }, props.delay)
    }

    const onMouseLeave = () => {
        if (isTouchDevice.value || isPinned.value) return
        clearShowTimeout()
        isHoverVisible.value = false
    }

    const onFocusIn = () => {
        if (isTouchDevice.value || isPinned.value) return
        decidePlacement()
        isHoverVisible.value = true
    }

    const onFocusOut = event => {
        if (isTouchDevice.value || isPinned.value) return
        if (wrapperRef.value?.contains(event.relatedTarget)) return
        isHoverVisible.value = false
    }

    const onDocumentClick = event => {
        if (!isOpen.value) return
        if (wrapperRef.value && !wrapperRef.value.contains(event.target)) {
            closeAll()
        }
    }

    const onKeydown = event => {
        if (isOpen.value && event.key === 'Escape') {
            closeAll()
            triggerRef.value?.focus()
        }
    }

    let mediaQueryList = null
    const handleMediaChange = e => { isTouchDevice.value = !e.matches }

    onMounted(() => {
        if (typeof window === 'undefined') return

        if (window.matchMedia) {
            mediaQueryList = window.matchMedia('(hover: hover) and (pointer: fine)')
            isTouchDevice.value = !mediaQueryList.matches
            mediaQueryList.addEventListener?.('change', handleMediaChange)
        }

        document.addEventListener('click', onDocumentClick)
        document.addEventListener('keydown', onKeydown)
        window.addEventListener('scroll', onScrollOrResize, { passive: true, capture: true })
        window.addEventListener('resize', onScrollOrResize)
    })

    onUnmounted(() => {
        clearShowTimeout()
        mediaQueryList?.removeEventListener?.('change', handleMediaChange)
        document.removeEventListener('click', onDocumentClick)
        document.removeEventListener('keydown', onKeydown)
        window.removeEventListener('scroll', onScrollOrResize, { capture: true })
        window.removeEventListener('resize', onScrollOrResize)
    })
</script>
